import { useMemo, useState, type Dispatch, type ReactNode } from 'react';

import {
  AppIcon,
  type FilterSelectOption,
  LabeledFilterMultiSelect,
  LabeledFilterSelect,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import type { RowAction } from '@backrs_ui/grow-ui/src/components/RowActionMenu/RowActionMenu';
import {
  groupStartStyle,
  menuItemContentStyle,
  menuItemIconStyle,
  menuPaperPropsSx,
} from '@backrs_ui/grow-ui/src/pages/Search/components/Filter/TopLevelMenuSelect';
import type {
  CoachingDashboardFiltersResponse,
  FilterCohort,
  FilterRole,
} from '@backrs_ui/grow-ui/src/redux/coachingDashboard';

import {
  Box,
  Button,
  InputAdornment,
  MenuItem,
  MenuList,
  Popover,
  type SxProps,
  TextField,
  type Theme,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  hasAnyCoachingDashboardFilter,
  VERIFIED_FILTER,
  type CoachingDashboardFilterAction,
  type CoachingDashboardFilterState,
} from './coachingDashboardFilters';

type CoachingDashboardFilterBarProps = Readonly<{
  searchValue: string;
  searchPlaceholder: string;
  onSearchChange: (value: string) => void;
  filters: CoachingDashboardFilterState;
  filterOptions: CoachingDashboardFiltersResponse | undefined;
  dispatch: Dispatch<CoachingDashboardFilterAction>;
  showInstitutionFilters: boolean;
  showStudentFilters: boolean;
  /** Roles the Role filter may offer, already scoped to the current roster. */
  roles: readonly FilterRole[];
  cohorts?: readonly FilterCohort[];
  showCohortFilter?: boolean;
  /** The in-scope institution field, or the notice shown when its options cannot be read. */
  institutionFilter?: ReactNode;
  /** Whether an institution is narrowed down to, which Clear also resets. */
  isInstitutionSelected?: boolean;
  selectionCount?: number;
  onClearSelection?: () => void;
  /** Every door the selection offers, in render order. One hierarchy across all of them:
   * a door that stands out reads as the one to take, and availability is what the
   * disabled state is for. */
  bulkActions?: readonly RowAction[];
}>;

export const CoachingDashboardFilterBar = ({
  searchValue,
  searchPlaceholder,
  onSearchChange,
  filters,
  filterOptions,
  dispatch,
  showInstitutionFilters,
  showStudentFilters,
  roles,
  cohorts = [],
  showCohortFilter = false,
  institutionFilter,
  isInstitutionSelected = false,
  selectionCount = 0,
  onClearSelection,
  bulkActions = [],
}: CoachingDashboardFilterBarProps) => {
  const { coachingDashboard } = appStrings.modules;
  const filterStrings = coachingDashboard.filters;

  const gradeOptions = useMemo<FilterSelectOption[]>(
    () => filterOptions?.grades ?? [],
    [filterOptions?.grades],
  );
  const schoolOptions = useMemo<FilterSelectOption[]>(
    () => (filterOptions?.schools ?? []).map((s) => ({ value: s, label: s })),
    [filterOptions?.schools],
  );
  const roleOptions = useMemo<FilterSelectOption[]>(
    () => roles.map((r) => ({ value: String(r.id), label: r.name })),
    [roles],
  );
  const programOptions = useMemo<FilterSelectOption[]>(
    () => (filterOptions?.programs ?? []).map((p) => ({ value: p.code, label: p.name })),
    [filterOptions?.programs],
  );
  const institutionRoleOptions = useMemo<FilterSelectOption[]>(
    () => (filterOptions?.institution_roles ?? []).map((r) => ({ value: r, label: capitalize(r) })),
    [filterOptions?.institution_roles],
  );
  const cohortOptions = useMemo<FilterSelectOption[]>(
    () => cohorts.map((c) => ({ value: String(c.id), label: c.name })),
    [cohorts],
  );

  const selectedRoleIdStrings = useMemo(() => filters.roleIds.map(String), [filters.roleIds]);

  const hasAnyFilter =
    hasAnyCoachingDashboardFilter(filters) || isInstitutionSelected || searchValue.trim() !== '';

  const showSelection = selectionCount > 0;
  const [moreFiltersAnchor, setMoreFiltersAnchor] = useState<HTMLElement | null>(null);
  const showProgramFilter =
    showStudentFilters && showInstitutionFilters && programOptions.length > 0;
  const hasMoreFilters = showProgramFilter || showStudentFilters;

  const moreFilterGroups = useMemo(() => {
    const groups: MoreFilterGroup[] = [];

    if (showProgramFilter) {
      groups.push({
        id: 'programs',
        options: programOptions,
        values: filters.programCodes,
        onToggle: (value) =>
          dispatch({ type: 'setProgramCodes', values: toggled(filters.programCodes, value) }),
      });
    }

    if (showStudentFilters) {
      groups.push({
        id: 'verified',
        options: VERIFIED_OPTIONS,
        values: filters.verified,
        onToggle: (value) =>
          dispatch({ type: 'setVerified', values: toggled(filters.verified, value) }),
      });
    }

    return groups;
  }, [
    dispatch,
    filters.programCodes,
    filters.verified,
    programOptions,
    showProgramFilter,
    showStudentFilters,
  ]);

  return (
    <Box id="coaching-dashboard-filter-bar" sx={filterBarSx}>
      <Box sx={searchGroupSx}>
        <TextField
          id="coaching-dashboard-search-input"
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          size="small"
          sx={searchInputSx}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <AppIcon
                  iconName="SearchMd"
                  diameter="20px"
                  color="Colors/Foreground/fg-quaternary"
                />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box id="coaching-dashboard-filter-fields" sx={fieldsRowSx}>
        {institutionFilter ? <Box sx={fieldSx}>{institutionFilter}</Box> : null}
        {showStudentFilters && gradeOptions.length > 0 && (
          <Box sx={gradeFieldSx}>
            <LabeledFilterMultiSelect
              id="coaching-dashboard-filter-grades"
              label={filterStrings.grade}
              placeholder={filterStrings.all}
              options={gradeOptions}
              values={filters.grades}
              onChange={(values) => dispatch({ type: 'setGrades', values })}
            />
          </Box>
        )}
        {showStudentFilters && schoolOptions.length > 0 && (
          <Box sx={fieldSx}>
            <LabeledFilterMultiSelect
              id="coaching-dashboard-filter-schools"
              label={filterStrings.school}
              placeholder={filterStrings.all}
              options={schoolOptions}
              values={filters.schools}
              onChange={(values) => dispatch({ type: 'setSchools', values })}
            />
          </Box>
        )}
        {showStudentFilters && !showInstitutionFilters && roleOptions.length > 0 && (
          <Box sx={fieldSx}>
            <LabeledFilterMultiSelect
              id="coaching-dashboard-filter-roles"
              label={filterStrings.role}
              placeholder={filterStrings.all}
              options={roleOptions}
              values={selectedRoleIdStrings}
              onChange={(values) => dispatch({ type: 'setRoleIds', values: values.map(Number) })}
            />
          </Box>
        )}
        {showInstitutionFilters && !showStudentFilters && institutionRoleOptions.length > 0 && (
          <Box sx={fieldSx}>
            <LabeledFilterMultiSelect
              id="coaching-dashboard-filter-institution-roles"
              label={filterStrings.institutionRole}
              placeholder={filterStrings.all}
              options={institutionRoleOptions}
              values={filters.institutionRoles}
              onChange={(values) => dispatch({ type: 'setInstitutionRoles', values })}
            />
          </Box>
        )}
        {showCohortFilter && cohortOptions.length > 0 && (
          <Box sx={fieldSx}>
            <LabeledFilterSelect
              id="coaching-dashboard-filter-cohort"
              label={filterStrings.cohort}
              placeholder={filterStrings.all}
              options={cohortOptions}
              value={filters.cohortId === null ? '' : String(filters.cohortId)}
              onChange={(value) =>
                dispatch({ type: 'setCohortId', value: value === '' ? null : Number(value) })
              }
            />
          </Box>
        )}
        {hasMoreFilters && (
          <Button
            id="coaching-dashboard-more-filters-btn"
            hierarchy="Secondary gray"
            size="md"
            sx={moreFiltersBtnSx}
            startIcon={<AppIcon iconName="FilterLines" diameter="20px" color="currentColor" />}
            onClick={(event) => setMoreFiltersAnchor(event.currentTarget)}
          >
            {filterStrings.moreFilters}
          </Button>
        )}
        {hasAnyFilter && (
          <Button
            id="coaching-dashboard-clear-filters-btn"
            hierarchy="Tertiary color"
            size="sm"
            sx={clearBtnSx}
            onClick={() => dispatch({ type: 'clear' })}
          >
            {filterStrings.clear}
          </Button>
        )}
      </Box>
      <Popover
        id="coaching-dashboard-more-filters"
        open={moreFiltersAnchor !== null}
        anchorEl={moreFiltersAnchor}
        onClose={() => setMoreFiltersAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: menuPaperPropsSx } }}
      >
        <MenuList>
          {moreFilterGroups.map((group, groupIndex) => [
            groupIndex > 0 ? <Box key={`${group.id}-divider`} sx={groupStartStyle} /> : null,
            ...group.options.map((option) => {
              const isChecked = group.values.includes(option.value);

              return (
                <MenuItem
                  key={`${group.id}-${option.value}`}
                  id={`coaching-dashboard-more-filters-${group.id}-${option.value}`}
                  role="menuitemcheckbox"
                  aria-checked={isChecked}
                  disableRipple
                  disableTouchRipple
                  onClick={() => group.onToggle(option.value)}
                >
                  <Box sx={menuItemContentStyle}>
                    <Box sx={menuItemIconStyle(isChecked)}>
                      <AppIcon
                        iconName={isChecked ? 'Check' : 'Square'}
                        diameter="16px"
                        color={isChecked ? 'Colors/Foreground/fg-white' : 'currentColor'}
                      />
                    </Box>
                    <Typography variant="text-md-medium">{option.label}</Typography>
                  </Box>
                </MenuItem>
              );
            }),
          ])}
        </MenuList>
      </Popover>
      {showSelection && (
        <Box id="coaching-dashboard-selection-bar" sx={selectionRowSx}>
          <Button
            id="coaching-dashboard-selection-count-btn"
            hierarchy="Secondary color"
            size="md"
            disableRipple
            sx={selectionCountBtnSx}
          >
            {coachingDashboard.bulkAssignSelectedCount(selectionCount)}
          </Button>
          <Button
            id="coaching-dashboard-selection-clear-btn"
            hierarchy="Secondary gray"
            size="md"
            onClick={onClearSelection}
          >
            {coachingDashboard.bulkAssignClearSelection}
          </Button>
          {bulkActions.map((action) => (
            <BulkActionButton key={action.id} action={action} />
          ))}
        </Box>
      )}
    </Box>
  );
};

/**
 * A disabled button emits no pointer events and takes no focus, so the reason hangs off a
 * wrapper that does both — without the tabIndex the reason is reachable by mouse alone,
 * and a keyboard reads the toolbar as shut with nothing said about why.
 */
const BulkActionButton = ({ action }: Readonly<{ action: RowAction }>) => {
  const button = (
    <Button
      id={action.id}
      hierarchy={BULK_ACTION_HIERARCHY}
      size="md"
      disabled={action.disabled}
      onClick={action.onSelect}
    >
      {action.label}
    </Button>
  );

  if (action.disabled !== true || action.disabledReason === undefined) return button;

  return (
    <Tooltip title={action.disabledReason} arrow>
      <Box component="span" tabIndex={0} aria-label={`${action.label} — ${action.disabledReason}`}>
        {button}
      </Box>
    </Tooltip>
  );
};

const BULK_ACTION_HIERARCHY = 'Secondary color' as const;

type MoreFilterGroup = Readonly<{
  id: string;
  options: readonly FilterSelectOption[];
  values: readonly string[];
  onToggle: (value: string) => void;
}>;

const toggled = (values: readonly string[], value: string): string[] =>
  values.includes(value) ? values.filter((held) => held !== value) : [...values, value];

const VERIFIED_OPTIONS: readonly FilterSelectOption[] = [
  {
    value: VERIFIED_FILTER.VERIFIED,
    label: appStrings.modules.coachingDashboard.filters.verifiedOnly,
  },
  {
    value: VERIFIED_FILTER.UNVERIFIED,
    label: appStrings.modules.coachingDashboard.filters.notVerified,
  },
];

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

const filterBarSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-end',
  flexShrink: 0,
  gap: backrs.spacing['spacing-xl'],
  marginBottom: backrs.spacing['spacing-3xl'],
  padding: backrs.spacing['spacing-lg'],
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary']}`,
  backgroundColor: backrs.colors['Beige/50'],
  '@container (min-width: 650px)': {
    padding: backrs.spacing['spacing-2xl'],
  },
});

const searchGroupSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-md'],
  flex: '1 1 240px',
  minWidth: 0,
});

const searchInputSx: SxProps<Theme> = () => ({
  flex: '1 1 240px',
  minWidth: 0,
});

const moreFiltersBtnSx: SxProps<Theme> = {
  flexShrink: 0,
  alignSelf: 'flex-end',
};

const selectionCountBtnSx: SxProps<Theme> = {
  cursor: 'default',
  pointerEvents: 'none',
};

const selectionRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-md'],
  flexBasis: '100%',
});

// The fields carry a label above the control, so a centred button would float off their line.
const clearBtnSx: SxProps<Theme> = {
  flexShrink: 0,
  alignSelf: 'flex-end',
};

const fieldsRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-end',
  gap: backrs.spacing['spacing-xl'],
  flex: '0 1 auto',
});

const fieldSx: SxProps<Theme> = {
  display: 'flex',
  width: 150,
};

// Wide enough for the longest grade option ("4th Year College") and the checkbox beside it.
const gradeFieldSx: SxProps<Theme> = {
  display: 'flex',
  width: 160,
};
